import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MobileGuidanceContext = createContext();

export const useMobileGuidance = () => {
  const ctx = useContext(MobileGuidanceContext);
  if (!ctx) return { isMobile: false, shouldGlow: () => false, markSeen: () => {}, hint: null };
  return ctx;
};

const detectRole = () => {
  const rolesStr = localStorage.getItem('user_roles');
  if (rolesStr) {
    try {
      const roles = JSON.parse(rolesStr);
      if (roles.includes('admin') || roles.includes('owner') || roles.includes('super_admin')) return 'business';
      if (roles.includes('technician')) return 'technician';
      if (roles.includes('contractor')) return 'contractor';
    } catch { /* fallthrough */ }
  }
  const single = localStorage.getItem('user_role');
  if (single === 'technician') return 'technician';
  if (single === 'contractor') return 'contractor';
  return 'business';
};

// Routes where guidance must NEVER appear
const GUIDANCE_BLOCKED_ROUTES = [
  '/login', '/signup', '/forgot-password', '/reset-password',
  '/terms-and-policy', '/privacy', '/portal',
  '/remodify/', '/customer-portal/',
  '/employee-portal', '/contractor-portal', '/pay-portal', '/scheduler-portal',
  '/super-admin/', '/analytics-login', '/verify-email', '/build-experience', '/membership', '/founders-checklist',
  '/pro/',
];

/*
 * GUIDANCE_CONFIG
 *
 * type:
 *   'pulse'     → expanding ring (FABs, primary CTAs)
 *   'glow'      → breathing glow (panels, modules)
 *   'shimmer'   → horizontal light pass (swipeable areas)
 *   'highlight' → scale + glow (home modules, key entries)
 *
 * duration: ms the CSS animation runs (finite, not infinite)
 * hint: optional 1-line text (shown as small floating pill)
 */
const GUIDANCE_CONFIG = {
  home: [
    { id: 'home_dispatch', selector: '[data-testid="module-dispatch"]', hint: 'Your jobs live here', type: 'highlight', roles: ['business'], delay: 400, duration: 6000 },
    { id: 'home_qi', selector: '[data-testid="module-remodquotes"]', type: 'highlight', roles: ['business'], delay: 1400, duration: 5000 },
    { id: 'home_remodpay', selector: '[data-testid="module-remodpay"]', type: 'highlight', roles: ['business'], delay: 2400, duration: 5000 },
    { id: 'home_analytics', selector: '[data-testid="module-analytics"]', type: 'highlight', roles: ['business'], delay: 3400, duration: 5000 },
    { id: 'home_settings', selector: '[data-testid="module-settings"]', type: 'glow', roles: ['business'], delay: 4400, duration: 6000 },
  ],
  dispatch: [
    { id: 'disp_fab', selector: '[data-tour="dispatch-fab"]', hint: 'Tap + to create a job', type: 'pulse', roles: ['business'], delay: 400, duration: 7000 },
    { id: 'disp_daystrip', selector: '[data-tour="dispatch-day-strip"]', type: 'shimmer', roles: null, delay: 1200, duration: 5000 },
    { id: 'disp_toolbar', selector: '[data-tour="dispatch-toolbar"]', type: 'glow', roles: null, delay: 2200, duration: 7000 },
    { id: 'disp_customers', selector: '[data-tour="dispatch-customers-btn"]', type: 'pulse', roles: ['business'], delay: 3400, duration: 6000 },
    { id: 'disp_clock', selector: '[data-tour="clock-toggle"]', hint: 'Clock in / out', type: 'pulse', roles: ['technician'], delay: 600, duration: 7000 },
    { id: 'disp_pay', selector: '[data-tour="toolbar-pay"]', hint: 'Your earnings', type: 'glow', roles: ['contractor', 'technician'], delay: 1400, duration: 7000 },
  ],
  invoices: [
    { id: 'inv_create', selector: '[data-testid="create-invoice-fab"]', hint: 'Create invoice or quote', type: 'pulse', roles: ['business'], delay: 400, duration: 5000 },
    { id: 'inv_tabs', selector: '[data-testid="invoice-section-tabs"]', type: 'glow', roles: ['business'], delay: 1400, duration: 5000 },
  ],
  messages: [
    { id: 'msg_new', selector: '[data-testid="new-message-btn"]', hint: 'New conversation', type: 'pulse', roles: ['business'], delay: 400, duration: 5000 },
    { id: 'msg_list', selector: '[data-testid="conversation-list"]', type: 'glow', roles: null, delay: 1400, duration: 5000 },
  ],
  moneyhub: [
    { id: 'money_tabs', selector: '[data-testid="money-hub-tabs"]', type: 'glow', roles: ['business'], delay: 400, duration: 5000 },
  ],
  wallet: [
    { id: 'wallet_toggle', selector: '[data-testid="wallet-finances-toggle"]', type: 'glow', roles: ['business'], delay: 400, duration: 5000 },
  ],
  settings: [
    { id: 'set_profile', selector: '[data-tour="settings-profile"]', hint: 'Start with your profile', type: 'glow', roles: ['business'], delay: 400, duration: 5000 },
    { id: 'set_tutorials', selector: '[data-tour="settings-tutorials"]', type: 'pulse', roles: null, delay: 1400, duration: 5000 },
  ],
};

export const MobileGuidanceProvider = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [seenIds, setSeenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('ari_guidance_seen') || '[]')); }
    catch { return new Set(); }
  });
  const [activeHint, setActiveHint] = useState(null); // {id, hint} — single hint only
  const [currentPage, setCurrentPage] = useState(null);
  const role = useRef(detectRole());
  const timersRef = useRef([]);
  const userId = localStorage.getItem('user_id');

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const persistSeen = useCallback((ids) => {
    localStorage.setItem('ari_guidance_seen', JSON.stringify([...ids]));
  }, []);

  const markSeen = useCallback((guidanceId) => {
    setSeenIds(prev => {
      const next = new Set(prev);
      next.add(guidanceId);
      persistSeen(next);
      return next;
    });
    setActiveHint(prev => (prev?.id === guidanceId ? null : prev));

    // Clean the CSS class from the element
    const el = document.querySelector(`[data-ari-guidance="${guidanceId}"]`);
    if (el) {
      el.classList.remove('ari-glow', 'ari-pulse', 'ari-shimmer', 'ari-highlight');
      el.removeAttribute('data-ari-guidance');
    }

    if (userId) {
      axios.post(`${API}/onboarding/progress/${userId}/complete`, { flow_id: guidanceId }).catch(() => {});
    }
  }, [persistSeen, userId]);

  const shouldGlow = useCallback((guidanceId) => {
    if (!isMobile) return false;
    // No glow after onboarding is completed
    if (localStorage.getItem('walkthrough_completed') === 'true') return false;
    return !seenIds.has(guidanceId);
  }, [isMobile, seenIds]);

  const activatePage = useCallback((pageName) => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
    setActiveHint(null);
    setCurrentPage(pageName);

    if (!isMobile) return;

    // Block ALL guidance if onboarding/walkthrough is already completed
    if (localStorage.getItem('walkthrough_completed') === 'true') return;

    // Block guidance on non-business portals and auth screens
    const currentPath = window.location.pathname;
    const isBlocked = GUIDANCE_BLOCKED_ROUTES.some(r => currentPath.startsWith(r)) || currentPath === '/';
    if (isBlocked) return;

    const config = GUIDANCE_CONFIG[pageName];
    if (!config) return;

    const currentRole = detectRole();
    role.current = currentRole;

    // Track first hint to show (the first one with a hint string)
    let hintShown = false;

    config.forEach(g => {
      if (g.roles && !g.roles.includes(currentRole)) return;
      if (seenIds.has(g.id)) return;

      // Timer to ADD animation class
      const showTimer = setTimeout(() => {
        const el = document.querySelector(g.selector);
        if (!el) return;

        el.classList.add(`ari-${g.type}`);
        el.setAttribute('data-ari-guidance', g.id);

        // Show the floating hint only for the first item that has one
        if (g.hint && !hintShown) {
          hintShown = true;
          setActiveHint({ id: g.id, hint: g.hint });

          // Auto-hide hint after 3.5s
          const hintTimer = setTimeout(() => {
            setActiveHint(prev => (prev?.id === g.id ? null : prev));
          }, 3500);
          timersRef.current.push(hintTimer);
        }
      }, g.delay);
      timersRef.current.push(showTimer);

      // Timer to REMOVE animation class (finite animation — clean up after duration)
      const removeTimer = setTimeout(() => {
        const el = document.querySelector(g.selector);
        if (el) {
          el.classList.remove(`ari-${g.type}`);
          el.removeAttribute('data-ari-guidance');
        }
      }, g.delay + (g.duration || 5000));
      timersRef.current.push(removeTimer);
    });
  }, [isMobile, seenIds]);

  const deactivate = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
    document.querySelectorAll('[data-ari-guidance]').forEach(el => {
      el.classList.remove('ari-glow', 'ari-pulse', 'ari-shimmer', 'ari-highlight');
      el.removeAttribute('data-ari-guidance');
    });
    setActiveHint(null);
    setCurrentPage(null);
  }, []);

  const resetAll = useCallback(() => {
    setSeenIds(new Set());
    localStorage.removeItem('ari_guidance_seen');
    if (userId) {
      axios.post(`${API}/onboarding/progress/${userId}/reset`, {}).catch(() => {});
    }
  }, [userId]);

  useEffect(() => {
    return () => { timersRef.current.forEach(t => clearTimeout(t)); };
  }, []);

  return (
    <MobileGuidanceContext.Provider value={{
      isMobile,
      shouldGlow,
      markSeen,
      activatePage,
      deactivate,
      resetAll,
      activeHint,
      currentPage,
      seenIds
    }}>
      {children}
    </MobileGuidanceContext.Provider>
  );
};
